<template>
  <div>
    <v-skeleton-loader
      v-if="pageLoading"
      type="table"
    />
    <section
      v-else
      class="content-snapshot"
      aria-label="Content snapshot"
    >
      <section class="content-analysis-top" aria-labelledby="job_content_heading">
        <div class="block-white">
          <h2 id="job_content_heading" class="text-h2 pb-1">
            Job Content
          </h2>
          <div class="sub-title">
            <p>
              Ensure your job is found and understood by as many qualified candidates as possible.
            </p>
          </div>
        </div>

        <div class="stat-links language-links">
          <div
            v-for="[title, value, text, ariaId] in [
              [
                'Benefits Language',
                contentStats.has_perks,
                'of jobs include information about your benefits.',
                'benefits_lang_card',
              ],
              [
                'Reporting Structure',
                contentStats.has_reporting_structure,
                'of jobs explain who the candidate will report to.',
                'reporting_struct_card',
              ],
              [
                'Diversity Statement',
                contentStats.has_eeoc,
                'of jobs include a diversity statement.',
                'diversity_stmt_card',
              ],
              [
                'Clear Job Location',
                contentStats.has_clean_location,
                'of jobs include a city and state/country.',
                'clear_job_location_card',
              ],
            ]"
            :key="title"
            class="stat-link"
          >
            <article class="language-stat" :aria-labelledby="ariaId">
              <h3 :id="ariaId" class="text-h2">
                {{ title }}
              </h3>
              <div class="language-graph">
                <ContentSnapshotDial
                  :value="value"
                  :is-smile="true"
                />
              </div>
              <div class="language-stat-text">
                <p>
                  {{ percent(value) }}
                  {{ text }}
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <div class="block-col-2">
        <div class="block-col">
          <article class="block-white" aria-labelledby="approachable_language_heading">
            <h2 id="approachable_language_heading" class="text-h2 pb-1">
              Approachable Language
            </h2>
            <div class="sub-title">
              <p>
                “You” and “Us” language is known to attract a more qualified
                and diverse candidate pool.
              </p>
            </div>
            <div class="lang-progress">
              <div class="lang-progress-line">
                <div class="lang-progress-label">
                  “You” <br>Language
                </div>
                <div class="lang-progress-in">
                  <div class="lang-progress-num">
                    {{ percent(contentStats.has_you_language) }}
                  </div>
                  <div class="lang-progress-block">
                    <div
                      class="progress-animation-horizontal"
                      :style="{
                        width: startAnimation
                          ? percent(contentStats.has_you_language)
                          : 0,
                        backgroundColor: contentStats.has_you_language >= 0.9
                          ? jobscoreHigh
                          : (contentStats.has_you_language >= 0.5
                            ? jobscoreMedium : jobscoreVeryLow),
                      }"
                    />
                  </div>
                </div>
                <div class="lang-progress-icon">
                  <div
                    class="smile single"
                    :class="[
                      contentStats.has_you_language >= 0.9
                        ? 'smile-good'
                        : (contentStats.has_you_language >= 0.5 ? 'smile-near-good' : 'smile-bad'),
                    ]"
                  />
                </div>
              </div>

              <div class="lang-progress-line">
                <div class="lang-progress-label">
                  “Us” <br>Language
                </div>
                <div class="lang-progress-in">
                  <div class="lang-progress-num">
                    {{ percent(contentStats.has_us_language) }}
                  </div>
                  <div class="lang-progress-block">
                    <div
                      class="progress-animation-horizontal"
                      :style="{
                        width: startAnimation
                          ? percent(contentStats.has_us_language)
                          : 0,
                        backgroundColor: contentStats.has_us_language >= 0.9
                          ? jobscoreHigh
                          : (contentStats.has_us_language >= 0.5
                            ? jobscoreMedium : jobscoreVeryLow),
                      }"
                    />
                  </div>
                </div>
                <div class="lang-progress-icon">
                  <div
                    class="smile single"
                    :class="[
                      contentStats.has_us_language >= 0.9
                        ? 'smile-good'
                        : (contentStats.has_us_language >= 0.5 ? 'smile-near-good' : 'smile-bad'),
                    ]"
                  />
                </div>
              </div>
            </div>
          </article>

          <article class="block-white" aria-labelledby="job_length_heading">
            <h2 id="job_length_heading" class="text-h2 pb-1">
              Job Length
            </h2>
            <div class="sub-title">
              <p>
                Job posts that are too long or too short are known to attract
                less qualified candidate pools.
              </p>
            </div>

            <div class="scale-graph-length">
              <div class="scale-graph">
                <DpplTooltip
                  v-for="[backgroundColor, color, value, title] in [
                    [jobscoreVeryLow, jobscoreVeryLow, jobLength.wayTooShort, 'Exceptionally Short'],
                    [jobscoreLow, jobscoreLow, jobLength.tooShort, 'Too Short'],
                    [bgDark, textDark, jobLength.justRight, 'Normal'],
                    [jobscoreLow, jobscoreLow, jobLength.tooLong, 'Too Long'],
                    [jobscoreVeryLow, jobscoreVeryLow, jobLength.wayTooLong, 'Exceptionally Long'],
                  ]"
                  :key="title"
                  tooltip-placement="bottom"
                  :tooltip-offset="8"
                  teleport
                >
                  <template #activator="{ onMouseenter, onMouseleave }">
                    <div
                      class="progress-animation-vertical"
                      :class="{ 'wider-graph': title === 'Normal' }"
                      :style="{
                        backgroundColor,
                        color,
                        height: startAnimation ? percent(value * jobLengthScale) : 0,
                      }"
                      @mouseenter="onMouseenter"
                      @mouseleave="onMouseleave"
                    >
                      <span>
                        {{ percent(value) }}
                      </span>
                      <div
                        class="scale-graph-tooltip"
                        data-toggle="tooltip"
                        data-placement="top"
                        data-html="true"
                        :data-title="title"
                      />
                    </div>
                  </template>
                  <template #tooltip-content>
                    <p class="text-body-3 mb-0 content-snapshot-tooltip">
                      {{ title }}
                    </p>
                  </template>
                </DpplTooltip>
              </div>
              <div class="scale-item">
                <div class="scale-line">
                  <div />
                  <div />

                  <div />

                  <div />
                  <div />
                </div>
                <div class="scale-text">
                  <div class="w96">
                    Too Short
                  </div>
                  <div class="w96">
                    Too Long
                  </div>
                </div>
              </div>
            </div>
          </article>
          <article class="block-white" aria-labelledby="job_titles_heading">
            <h2 id="job_titles_heading" class="text-h2 pb-1">
              Job Titles
            </h2>
            <div class="sub-title">
              <p>
                Clear job titles improve the findability of your job as well
                as the quality of your candidate pool.
              </p>
            </div>

            <div class="titles-blocks">
              <div
                v-for="[id, title, description] in [
                  [
                    '36',
                    'Includes Keywords',
                    'of jobs include all necessary keywords for candidate search.',
                  ],
                  [
                    '38',
                    'Clear Seniority',
                    'of jobs correctly signify seniority with years or experience.',
                  ],
                  [
                    '37',
                    'Clear Word Choice',
                    'of jobs do not include any confusing acronyms or job titles.',
                  ],
                ]"
                :key="id"
                class="titles-line"
              >
                <div class="titles-graph">
                  <ContentSnapshotDial
                    :value="(issueCategoryIds[id] || 1)"
                    :is-smile="false"
                  />
                </div>
                <div class="titles-text">
                  <p class="text-h3 pb-1">
                    {{ title }}
                  </p>
                  <p>
                    {{ percent(issueCategoryIds[id] || 0, '100%') }} {{ description }}
                  </p>
                </div>
              </div>
            </div>
          </article>
        </div>

        <div class="block-col">
          <article
            v-if="!authData.inclusionMeterDisabled"
            class="block-white gendered-chart-wrap"
            aria-labelledby="inclusive_content_heading"
          >
            <h2 id="inclusive_content_heading" class="text-h2 pb-1">
              Inclusive Content
            </h2>
            <div class="sub-title">
              <p>
                Avoid language and content issues that are known to reduce overall
                applicant diversity.
              </p>
            </div>
            <div class="scale-graph">
              <div
                v-for="([color, smile], i) in [
                  [jobscoreHigh, 'smile-good'],
                  [basicYellow, 'smile-normal'],
                  [jobscoreLow, 'smile-near-bad'],
                  [jobscoreVeryLow, 'smile-bad'],
                  [jobscoreVeryLow, 'smile-bad'],
                ]"
                :key="i"
                class="progress-animation-vertical"
                :style="{
                  backgroundColor: color,
                  color,
                  height: startAnimation
                    ? percent(genderScore[i] / genderScaleFactor)
                    : 0,
                }"
              >
                <span class="inclusive-bar-label">
                  {{ percent(genderScore[i]) }}
                </span>
                <div
                  :class="['smile', smile]"
                  :style="{ backgroundColor: color }"
                />
              </div>
            </div>

            <div class="scale-item">
              <div class="scale-line inclusive-scale-line">
                <div class="scale-text">
                  Inclusive
                </div>
                <div class="scale-line-fullline">
                  <span />
                </div>
                <div class="scale-text">
                  Exclusive
                </div>
              </div>
            </div>
          </article>

          <article class="block-white pool-table" aria-labelledby="candidate_pool_heading">
            <h2 id="candidate_pool_heading" class="text-h2 pb-1">
              Candidate Pool
            </h2>
            <div class="sub-title">
              <p>
                Unnecessary or unusual job requirements can impact the size of your job's
                estimated candidate pool.
              </p>
            </div>

            <div class="candidate-row">
              <div class="candidate-col-large">
                <div class="candidate-item-row">
                  <div
                    v-for="[id, image, title, alt] in [
                      ['2', 'small', 'Small', 'Small candidate pool'],
                      ['3', 'medium', 'Medium', 'Medium candidate pool'],
                      ['4', 'large', 'Large', 'Large candidate pool'],
                    ]"
                    :key="id"
                    class="candidate-item-col"
                  >
                    <div class="candidate-num">
                      {{ percent(specificity[id]) }}
                    </div>
                    <div class="candidate-image">
                      <img
                        :src="`https://static.datapeople.io/candidate-pool/global-${image}.svg`"
                        :alt="alt"
                      >
                    </div>
                    <div class="candidate-description">
                      {{ title }}
                    </div>
                  </div>
                </div>
                <div class="candidate-title">
                  Global
                </div>
              </div>

              <div class="candidate-col-small">
                <div class="candidate-num">
                  {{ percent(specificity['1']) }}
                </div>
                <div class="candidate-image">
                  <img
                    src="https://static.datapeople.io/candidate-pool/local-small.svg"
                    alt="Small candidate pool"
                  >
                </div>
                <div class="candidate-description">
                  Small
                </div>
                <div class="candidate-title">
                  Local
                </div>
              </div>
            </div>
          </article>

          <article class="block-white" aria-labelledby="job_age_heading">
            <h2 id="job_age_heading" class="text-h2 pb-1">
              Job Age
            </h2>
            <div class="sub-title">
              <p>
                Jobs that have been live for more than 60 days underperform on search engines.
              </p>
            </div>

            <div class="days-table">
              <div class="days-title">
                Days
              </div>
              <DpplTooltip
                v-for="([label, bgColor, color, borderColor], i) in [
                  ['10', bgDark, textDark, bgDark],
                  ['20', bgDark, textDark, bgDark],
                  ['30', bgDark, textDark, bgDark],
                  ['40', bgDark, textDark, bgDark],
                  ['50', jobscoreLow, textDark, jobscoreLow],
                  ['60+', jobscoreVeryLow, textDark, jobscoreVeryLow],
                ]"
                :key="label"
                tooltip-placement="top"
                :tooltip-offset="8"
                teleport
              >
                <template #activator="{ onMouseenter, onMouseleave }">
                  <div
                    class="days-num"
                    :style="{ color }"
                    @mouseenter="onMouseenter"
                    @mouseleave="onMouseleave"
                  >
                    <div
                      class="days-scale"
                      :style="{ background: borderColor }"
                    />
                    <div class="days-circle">
                      <div
                        class="progress-animation-vertical"
                        :style="{
                          background: bgColor,
                          height: startAnimation ? percent(jobAge[i] / jobAgeScaleFactor) : 0,
                        }"
                      />
                    </div>
                    <span>
                      {{ label }}
                    </span>
                  </div>
                </template>
                <template #tooltip-content>
                  <p class="text-body-3 mb-0 content-snapshot-tooltip">
                    {{ totalJobsByAge[i] }}
                  </p>
                </template>
              </DpplTooltip>
            </div>
          </article>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppLoading } from '@/stores/app/loading';
import ContentSnapshotDial from '@/components/insights/OperationalInsights/ContentSnapshot/ContentSnapshotDial.vue';
import DpplTooltip from '@/components/datapeople/DpplTooltip.vue';
import { useContentSnapshot } from '@/stores/insights/contentSnapshot';
import { useAppBase } from '@/stores/app/base';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useFilters } from '@/stores/filters/filters';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { useColors } from '@/composables/colors';
import { useInsights } from '@/stores/insights/insights';
import { useInsightsShare } from '@/stores/insights/share';

export default {
  name: 'ContentSnapshot',
  components: {
    ContentSnapshotDial,
    DpplTooltip,
  },
  emits: ['track-page-load'],
  setup() {
    const {
      jobscoreHigh,
      jobscoreMedium,
      jobscoreVeryLow,
      jobscoreLow,
      textDark,
      bgDark,
      basicYellow,
    } = useColors();

    return {
      jobscoreHigh,
      jobscoreMedium,
      jobscoreVeryLow,
      jobscoreLow,
      textDark,
      bgDark,
      basicYellow,
    };
  },
  data() {
    return {
      startAnimation: false,
    };
  },
  computed: {
    ...mapState(useAppAuthData, ['authData']),
    ...mapState(useAppLoading, ['pageLoading']),
    ...mapState(useContentSnapshot, ['contentStats', 'totals']),
    ...mapState(useDatePicker, ['dateWeekIndex']),

    jobLength() {
      const { job_length: length } = this.contentStats || {};
      const {
        way_too_short: wayTooShort, too_short: tooShort, just_right: justRight, too_long: tooLong,
        way_too_long: wayTooLong,
      } = length || {};

      return {
        wayTooShort, tooShort, justRight, tooLong, wayTooLong,
      };
    },

    jobLengthScale() {
      const { job_length: jobLength } = this.contentStats;
      const jobLengthValues = Object.keys(jobLength || {}).map((key) => jobLength[key]);
      const maxJobLength = Math.max.apply(null, jobLengthValues) || 1;
      return 0.9 / maxJobLength;
    },

    genderScore() {
      return this.contentStats.gender_score || [];
    },

    genderScaleFactor() {
      const { gender_score: genderScore } = this.contentStats;
      const maxGenderScore = Math.max.apply(null, genderScore) || 1;
      return maxGenderScore / 0.8;
    },

    issueCategoryIds() {
      return this.contentStats.issue_category_ids || {};
    },

    specificity() {
      return this.contentStats.specificity || {};
    },

    jobAge() {
      return this.contentStats.job_age || [];
    },

    jobAgeScaleFactor() {
      const { job_age: jobAge } = this.contentStats;
      const maxJobAge = Math.max.apply(null, jobAge) || 1;
      return maxJobAge / 0.8;
    },

    totalJobsByAge() {
      return this.contentStats.total_jobs_by_age || [];
    },
  },

  async mounted() {
    this.setShowInternalDefault(true);

    subscribeToFilterUpdates(this.loadPage);

    this.$emit('track-page-load', { page: 'Content Snapshot' });
    this.setFilterConfig({
      module: 'analytics/contentSnapshot',
      datepicker: true,
      showMyJobs: !window?.sessionStorage?.reportingShareKey,
      subMenuType: null,
    });

    const isSharedPage = window.sessionStorage.reportingShareKey;

    this.showFilterBarSettings({ route: this.$route.name, options: [] });

    if (!isSharedPage) {
      this.setShareConfig({
        shareable: true,
      });
    }

    await this.loadPage({
      router: this.$router,
      routeQuery: this.$route.query,
      selectedMyJobs: this.$route.query?.owned === 'true',
      selectedDateWeek: this.dateWeekIndex || this.$route.query?.week,
    });

    this.setDatePickerVariant({ variant: 'week' });

    this.startAnimation = false;
    window.setTimeout(() => {
      this.startAnimation = true;
    }, 300);
  },

  beforeUnmount() {
    unsubscribeToFilterUpdates();

    this.resetShareConfig();

    this.clearPersistentFilters();
    this.setDatePickerVariant({ resetDateRange: true });
  },

  methods: {
    ...mapActions(useContentSnapshot, ['loadPage']),
    ...mapActions(useFilters, ['setFilterConfig', 'clearPersistentFilters']),
    ...mapActions(useDatePicker, ['setDatePickerVariant']),
    ...mapActions(useAppBase, ['showFilterBarSettings']),
    ...mapActions(useInsights, ['setShowInternalDefault']),
    ...mapActions(useInsightsShare, ['setShareConfig', 'resetShareConfig']),
    percent(value, fallbackPercent = '0%') {
      return (value ? `${Math.round(value * 100)}%` : fallbackPercent);
    },
  },
};
</script>

<style scoped lang="less">
.content-snapshot {
  .content-analysis-top {
    background: @white;
    border: 1px solid @stroke-default;
    border-radius: 8px;
    margin-bottom: 12px;
    .block-white {
      box-shadow: none;
      margin-bottom: 0;
      border: none;
      border-bottom: 1px solid @stroke-default;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      padding-bottom: 12px;
    }
  }
  .stat-links {
    box-shadow: none;
    margin-bottom: 0;
    background: @white;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
  .stat-link {
    border-color: @stroke-default;
  }
  .sub-title {
    font-weight: 300;
    font-size: 16px;
    line-height: 20px;
    p {
      &:last-child {
        margin: 0;
      }
    }
  }
  h3, .h3 {
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    color: @text-darkest;
    padding: 0 0 4px 0;
    margin: 0;
  }
  .language-links {
    margin-bottom: 6px;
    border: none;
    .stat-link {
      align-items: flex-start;
    }
  }
  .language-stat {
    width: 100%;
    h4, .h4 {
      font-weight: 500;
      font-size: 16px;
      line-height: 20px;
      padding-bottom: 8px;
    }
  }
  .language-stat-text {
    font-weight: 300;
    font-size: 16px;
    line-height: 20px;
    color: @text-darkest;
    p {
      &:last-child {
        margin: 0;
      }
    }
  }
  .language-graph {
    margin: 0 0 4px 0;
  }
  .stat-links {
    display: flex;
    background: @white;
    border-radius: 8px;
    margin-bottom: 12px;
    &.margin-top {
      margin-top: 20px;
    }
  }
  .stat-link {
    flex: 1;
    display: flex;
    align-items: center;
    font-weight: 300;
    font-size: 16px;
    line-height: 20px;
    color: @text-darkest;
    min-height: 125px;
    padding: 24px;
    border-left: 1px solid @stroke-default;
    position: relative;
    &:first-child {
      border-left-color: transparent;
    }
  }
  .block-white {
    background: @white;
    padding: 24px;
    margin-bottom: 1px;
    border: 1px solid @stroke-default;
    border-radius: 8px;
    > p {
      &:last-child {
        margin: 0;
      }
    }
    &.border-bottom {
      border-bottom: 2px solid @stroke-default;
    }
  }
  .block-col-2 {
    display: flex;
    .block-col {
      flex: 1;
      margin-right: 12px;
      &:last-child {
        margin: 0;
      }
    }
    .block-white {
      margin-bottom: 12px;
    }
  }
  .lang-progress {
    margin: 24px 0 0 0;
  }
  .lang-progress-line {
    display: flex;
    &:last-child {
      .lang-progress-label {
        padding-bottom: 0;
      }
    }
  }
  .lang-progress-icon {
    margin: 20px 15px -10px;
    font-size: 20px;
    width: 20px;
    flex: 0 auto;
    .icon-skills {
      color: @basic-blue;
    }
    .icon-exclamation {
      color: @basic-red;
    }
  }
  .lang-progress-in {
    flex: 1;
  }
  .lang-progress-label {
    flex: 0 auto;
    text-align: right;
    width: 100px;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    color: #000;
    margin: 0 16px 0 0;
    padding: 0 16px 24px 0;
    border-right: 1px solid @stroke-default;
  }
  .lang-progress-num {
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    padding-bottom: 8px;
  }

  .lang-progress-block {
    background: rgba(@grey-900, .1);
    height: 8px;
    border-radius: 6px;
    > div {
      height: 12px;
      border-radius: 6px;
      top: -2px;
      position: relative;
    }
  }

  .progress-animation-horizontal, .progress-animation-vertical {
    -webkit-transition: all 1s ease-in;
    transition: all 1s ease-in;
  }

  .gendered-chart-wrap .inclusive-bar-label {
    transform: translateY(-20px);
  }

  :deep(.smile) {
    width: 2px;
    height: 8px;
    display: block;
    margin: -7px auto 0;
    position: relative;

    &:after {
      content: "";
      width: 20px;
      height: 20px;
      position: absolute;
      left: 50%;
      top: -20px;
      margin-left: -10px;
      background-size: cover !important;
    }

    &.smile-bad {
      &:after {
        background: url("../../../../assets/smiles/smile-bad.svg") center center no-repeat;
      }
    }

    &.smile-near-bad {
      &:after {
        background: url("../../../../assets/smiles/smile-near-bad.svg") center center no-repeat;
      }
    }

    &.smile-normal {
      &:after {
        background: url("../../../../assets/smiles/smile-normal.svg") center center no-repeat;
      }
    }

    &.smile-near-good {
      &:after {
        background: url("../../../../assets/smiles/smile-near-good.svg") center center no-repeat;
      }
    }

    &.smile-good {
      &:after {
        background: url("../../../../assets/smiles/smile-good.svg") center center no-repeat;
      }
    }

    &.single {
      width: 20px;
      height: 20px;
      background: none;

      &:after {
        left: 50%;
        top: 50%;
        margin: -10px 0 0 -10px;
      }
      margin: 0 0 0 8px;
    }
  }

  .scale-graph-length {
    width: 280px;
    margin: 0 auto;
    .scale-graph {
      width: 280px;
      height: 100px;

      :deep(.dppl-tooltip) {
        height: 100%;
        background: transparent;
        border-radius: 0;

        &:has(.wider-graph) {
          flex: 0 0 90px;
          width: 90px;
        }
      }

      :deep(.activator) {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: flex-end;
      }

      :deep(.progress-animation-vertical) {
        width: 100%;
        position: relative;
        border-radius: 3px;
      }

      .wider-graph {
        width: 90px;
        flex: 0 auto;
        background: rgba(@grey-900, .1);
      }
    }
    .scale-item {
      padding: 0;
    }
    .scale-text {
      padding: 12px 2px 0;
      font-weight: 300;
      font-size: 12px;
      line-height: 16px;
    }
    .scale-line {
      > div {
        height: 12px;
        border-radius: 3px;
        &:nth-child(1) {
          background: @basic-red;
        }
        &:nth-child(2) {
          background: @basic-orange;
        }
        &:nth-child(3) {
          background: rgba(@grey-900, .1);
          width: 90px;
          flex: 0 auto;
        }
        &:nth-child(4) {
          background: @basic-orange;
        }
        &:nth-child(5) {
          background: @basic-red;
        }
      }
    }
  }
  .scale-graph {
    display: flex;
    align-items: flex-end;
    margin: 40px auto 4px;
    width: 280px;
    height: 100px;
    > div {
      flex: 1;
      margin: 0 2px;
      position: relative;
      background: @grey-700;
      border-radius: 3px;
      span {
        position: absolute;
        left: 0;
        right: 0;
        top: -27px;
        text-align: center;
        font-weight: 500;
        font-size: 16px;
        line-height: 19px;
        color: @text-darkest;
      }
    }
  }
  .scale-line-fullline {
    height: 12px;
    background: linear-gradient(
      270deg,
      @basic-red 18.75%,
      @basic-orange 48.07%,
      @basic-yellow 80.21%,
      @basic-green 100%
    );
    border-radius: 10px !important;
  }
  .scale-item {
    padding: 0;
    margin: 0 0 20px;
    position: relative;
  }
  .scale-text {
    display: flex;
    justify-content: space-between;
    padding: 6px 0 8px 0;
    > div {
      flex: 1;
      text-align: center;
      &:first-child {
        text-align: left;
      }
      &:last-child {
        text-align: right;
      }
    }
    .link-text {
      &:hover {
        color: @black;
        text-decoration: underline;
      }
    }
    .w96 {
      flex: 0 auto;
      width: 96px;
      text-align: center !important;
    }
  }
  .scale-line {
    display: flex;
    > div {
      flex: 1;
      margin: 0 2px;
      position: relative;
      border-radius: 3px;
      span {
        display: block;
        height: 33px;
        position: relative;
        overflow: hidden;
        &:before {
          content: "";
          position: absolute;
          left: -50px;
          right: -50px;
          height: 33px;
        }
      }

      &:nth-child(1) {
        span {
          &:before {
            bottom: -18px;
            background: #0BB88B;
            opacity: .8;
          }
        }
      }
      &:nth-child(2) {
        span {
          &:before {
            bottom: -20px;
            background: #0BB88B;
            opacity: .5;
          }
        }
      }
      &:nth-child(3) {
        span {
          &:before {
            bottom: -22px;
            background: #DF575A;
            opacity: .3;
          }
        }
      }
      &:nth-child(4) {
        span {
          &:before {
            bottom: -24px;
            background: #DF575A;
            opacity: .5;
          }
        }
      }
      &:nth-child(5) {
        span {
          &:before {
            bottom: -26px;
            background:#DF575A;
            opacity: .8;
          }
        }
      }
      &:nth-child(6) {
        span {
          &:before {
            top: -11px;
            background: rgba(223,87,90,.7);
          }
        }
      }
      &:nth-child(7) {
        span {
          &:before {
            top: -9px;
            background: rgba(223,87,90,.8);
          }
        }
      }
      &:nth-child(8) {
        span {
          &:before {
            top: -7px;
            background: rgba(223,87,90,.9);
          }
        }
      }
      &:nth-child(9) {
        span {
          &:before {
            top: -5px;
            background: #DF575A;
          }
        }
      }
      &.active {
        &:after {
          content: "";
          position: absolute;
          left: 50%;
          margin-left: -5px;
          border-width: 10px 5px;
          border-style: solid;
          border-color: @black transparent transparent transparent;
        }
        ~ div {
          span {
            &:before {
              background: @white;
            }
          }
        }
      }
      &:nth-child(1).active:after {
        top: 4px;
      }
      &:nth-child(2).active:after {
        top: 6px;
      }
      &:nth-child(3).active:after {
        top: 8px;
      }
      &:nth-child(4).active:after {
        top: 10px;
      }
      &:nth-child(5).active:after {
        top: 12px;
      }
    }
  }
  .titles-blocks {
    margin-top: 30px;
  }
  .titles-line {
    padding-bottom: 24px;
    display: flex;
    font-weight: 300;
    font-size: 16px;
    line-height: 20px;
    align-items: center;
    &:last-child {
      padding-bottom: 0;
    }
  }
  .titles-graph {
    flex: 0 auto;
    width: 104px;
  }
  .titles-text {
    flex: 1;
    h4 {
      font-weight: 500;
      font-size: 16px;
      line-height: 20px;
      padding: 0 0 4px;
    }
    p {
      &:last-child {
        padding: 0;
        margin: 0;
      }
    }
  }
  .gendered-chart-wrap {
    flex: 1;
    background: #fff;
    padding: 16px 22px 24px;
    .scale-graph {
      width: 280px;
      max-width: 100%;
      margin: 50px auto 4px;
    }
    .scale-item {
      width: 280px;
      margin: 0 auto 10px;
      padding: 0;
    }
    .scale-line.inclusive-scale-line > .scale-text {
        position: absolute;
        top: 50%;
        padding: 0;
        transform: translateY(-50%);

        &:first-child {
          right: calc(100% + 8px);
        }

        &:last-child {
          left: calc(100% + 8px);
        }
      }
    .scale-line {
      > div {
        &.active {
          &:after {
            display: none;
          }
        }
        span {
          height: 13px;
          &:before {
            height: 45px;
            display: none;
          }
        }
      }
    }
    .scale-text {
      padding: 4px 2px 0;
      font-size: 12px;
    }
    &.block-white {
      padding: 16px 22px;
    }
  }
  .candidate-row {
    margin: 24px 0 0;
    display: flex;
    justify-content: center;
    font-weight: 300;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }
  .candidate-col-large {
    width: 192px;
    min-width: 192px;
    flex: 0 auto;
  }
  .candidate-col-small {
    margin-left: 48px;
    width: 64px;
    min-width: 64px;
    flex: 0 auto;
  }

  .candidate-item-row {
    display: flex;
  }
  .candidate-item-col {
    flex: 1;
  }
  .candidate-num {
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    margin-bottom: 10px;
  }
  .candidate-image {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
  }
  .candidate-description {
    margin-bottom: 16px;
  }
  .candidate-title {
    background: rgba(@grey-900, .05);
    border-radius: 3px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
    color: rgba(@grey-900, .5)
  }
  .days-table {
    display: flex;
    padding-top: 110px;
    position: relative;
    width: 288px;
    margin: 0 auto;

    :deep(.dppl-tooltip) {
      flex: 1;
      margin: 0 0 0 4px;
    }
  }
  .days-title {
    flex: 0 auto;
    position: absolute;
    left: -27px;
    bottom: 0;
    font-size: 12px;
  }
  .days-num {
    flex: 1;
    text-align: center;
    position: relative;
    font-weight: 300;
    font-size: 12px;
    line-height: 16px;
    color: @text-darkest;
    padding-top: 20px;
  }
  .days-scale {
    height: 12px;
    border-radius: 3px;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }
  .days-circle {
    position: absolute;
    left: 0;
    bottom: 40px;
    width: 100%;
    height: 100px;
    display: flex;
    align-items: flex-end;
    > div {
      width: 100%;
      position: relative;
      border-radius: 3px;
    }
  }
}

.content-snapshot-tooltip {
  color: @text-dark;
}
</style>
